import { createContext, useContext, useEffect, useState } from 'react';
import api, { mediaUrl } from '../lib/api';
import { applyTheme, DEFAULT_THEME } from '../lib/themes';
import { FALLBACK_LOGO, FALLBACK_NAME, displayBrandName, isPrimaryBrandHost, revealTenantBoot } from '../lib/brand';

export const DEFAULT_MODULES = {
  ticker: true, logo_marquee: true, brand_hero: true, banners: true, sponsors_main: true, sponsors_vip: true, sponsors_trusted: true,
  games: true, tasks: true, market: true, tournaments: true, leaderboard: true, news: true, wheel: true, promo: true,
};

const SettingsContext = createContext({
  modules: DEFAULT_MODULES,
  setModules: () => {},
  site: { slider_interval: 5, theme: DEFAULT_THEME, brand_name: '', brand_logo: '' },
  setSite: () => {},
});

const applyBrandChrome = (site) => {
  try {
    const name = displayBrandName(site?.brand_name);
    if (name) document.title = name;
    const raw = String(site?.brand_logo || '').trim();
    const href = raw ? mediaUrl(raw) : (isPrimaryBrandHost() && (!site?.brand_name || String(site.brand_name).trim() === FALLBACK_NAME) ? FALLBACK_LOGO : '');
    if (!href) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  } catch (_) { /* favicon/title is best-effort */ }
};

export const SettingsProvider = ({ children }) => {
  const primary = isPrimaryBrandHost();
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [site, setSite] = useState({ slider_interval: 5, theme: '', brand_name: '', brand_logo: '' });
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    const t = window.setTimeout(() => {
      if (!live) return;
      try {
        const host = (window.location.hostname || '').replace(/^www\./, '').toLowerCase();
        const cached = host ? localStorage.getItem('bh_theme:' + host) : '';
        if (cached) applyTheme(cached, { broadcast: false });
      } catch { /* ignore */ }
      setReady(true);
      revealTenantBoot();
    }, 2500);
    Promise.all([
      api.get('/settings/modules').catch(() => ({ data: DEFAULT_MODULES })),
      api.get('/settings/site').catch(() => ({ data: null })),
    ]).then(([modRes, siteRes]) => {
      if (!live) return;
      window.clearTimeout(t);
      if (modRes?.data) setModules(modRes.data);
      const next = siteRes?.data;
      if (next) {
        setSite(next);
        if (next.theme) applyTheme(next.theme);
      }
      applyBrandChrome(next || {});
      setReady(true);
      revealTenantBoot();
    });
    return () => { live = false; window.clearTimeout(t); };
  }, []);
  useEffect(() => {
    if (!ready || !site?.theme) return;
    applyTheme(site.theme);
  }, [site?.theme, ready]);
  useEffect(() => {
    if (!ready) return;
    applyBrandChrome(site);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.brand_name, site?.brand_logo, ready]);
  if (!ready) return null;
  return <SettingsContext.Provider value={{ modules, setModules, site, setSite }}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => useContext(SettingsContext);
